import React, { useState, useEffect } from "react";
import axios from "axios";
//components
import Button from "../components/Button";
import Header from "../components/Header";
import ScrollOnNavigation from "../components/ScrollOnNavigation";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ConsentCheckboxes from "../components/ConsentCheckboxes";
//styles
import {
  StyledContact,
  StyledContainer,
  StyledForm,
} from "../styles/pages/contact";
//files and images
import { rzetelna } from "../img";
//animations
import { motion } from "framer-motion";
import {
  FadeLeftAnimation,
  FadeRightAnimation,
  PagesAnimation,
} from "../styles/Animations";
//languages
import { useLanguage } from "../hooks/useLanguage";
import type { LanguageProps } from "../typings";
//links
import { mapsUrl } from "../constants/links";

const Contact = ({ language }: LanguageProps) => {
  const formSwal = withReactContent(Swal);

  const lang = useLanguage(language);

  //form handler
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");

  useEffect(() => {
    setNameError("");
    setEmailError("");
    setSubjectError("");
    setMessageError("");
  }, [language]);

  const NameInputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };
  const CompanyInputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompany(event.target.value);
  };
  const EmailInputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };
  const SubjectInputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(event.target.value);
  };
  const MessageInputHandler = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setMessage(event.target.value);
  };

  const SubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let failed = false;

    //check name
    if (name === "") {
      failed = true;
      setNameError(lang.contact.formNameError1);
    } else if (name.length <= 1) {
      failed = true;
      setNameError(lang.contact.formNameError2);
    } else if (
      !/^[a-zA-Zа-яА-Я, ,\-,ł,Ł,ę,Ę,ą,Ą,ó,Ó,ż,Ż,ź,Ź,ć,Ć,ś,Ś,n,Ń,å,Å,ä,Ä,ö,Ö,ü,Ü,ß,ẞ]*$/g.test(
        name,
      )
    ) {
      failed = true;
      setNameError(lang.contact.formNameError3);
    } else {
      setNameError("");
    }

    //check email
    if (email === "") {
      failed = true;
      setEmailError(lang.contact.formEmailError1);
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
      failed = true;
      setEmailError(lang.contact.formEmailError2);
    } else {
      setEmailError("");
    }

    //check subject
    if (subject === "") {
      failed = true;
      setSubjectError(lang.contact.formSubjectError1);
    } else if (subject.length <= 1) {
      failed = true;
      setSubjectError(lang.contact.formSubjectError2);
    } else {
      setSubjectError("");
    }

    //check message
    if (message === "") {
      failed = true;
      setMessageError(lang.contact.formMessageError1);
    } else if (message.length <= 1) {
      failed = true;
      setMessageError(lang.contact.formMessageError2);
    } else {
      setMessageError("");
    }

    if (failed) {
      return false;
    } else {
      const data = {
        name: name,
        company: company,
        email: email,
        subject: subject,
        message: message,
        site: "AgwiPol Jobs",
      };
      setName("");
      setCompany("");
      setEmail("");
      setSubject("");
      setMessage("");
      axios({
        method: "POST",
        url: "/api/contact",
        data: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          console.log("Good request: " + response);
          formSwal.fire({
            title: (
              <h2
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formSuccessTitle}
              </h2>
            ),
            html: (
              <div
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formSuccessText}
              </div>
            ),
            icon: "success",
            confirmButtonText: lang.index.formErrorButton,
            scrollbarPadding: false,
            backdrop: `
                rgba(0, 0, 0, 0.4)
                `,
          });
        })
        .catch((error) => {
          console.log("Bad requst: " + error);
          formSwal.fire({
            title: (
              <h2
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formErrorTitle}
              </h2>
            ),
            html: (
              <div
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formErrorText}
              </div>
            ),
            icon: "error",
            confirmButtonText: lang.index.formErrorButton,
            confirmButtonColor: "rgb(218, 0, 38)",
            scrollbarPadding: false,
            backdrop: `
                rgba(0, 0, 0, 0.4)
                `,
          });
        });
    }
  };

  const [consent, setConsent] = useState({
    data: false,
    doc: false,
  });

  return (
    <motion.div
      variants={PagesAnimation}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <Header language={language} />

      <StyledContact className="contact">
        <StyledContainer>
          <motion.div className="left" variants={FadeRightAnimation}>
            <div className="form-container">
              <StyledForm onSubmit={SubmitHandler}>
                <div className="name-company form-inner">
                  <div className="name">
                    <label htmlFor="name">{lang.contact.formName}</label>
                    <input
                      onChange={NameInputHandler}
                      className={nameError.length !== 0 ? "has-error" : ""}
                      value={name}
                      type="text"
                      id="name"
                    />
                    {nameError.length !== 0 && (
                      <span className="error">{nameError}</span>
                    )}
                  </div>
                  <div className="company">
                    <label htmlFor="company">{lang.contact.formCompany}</label>
                    <input
                      onChange={CompanyInputHandler}
                      value={company}
                      type="text"
                      id="company"
                    />
                  </div>
                </div>
                <div className="email form-inner">
                  <label htmlFor="email">{lang.contact.formEmail}</label>
                  <input
                    onChange={EmailInputHandler}
                    className={emailError.length !== 0 ? "has-error" : ""}
                    value={email}
                    type="email"
                    id="email"
                  />
                  {emailError.length !== 0 && (
                    <span className="error">{emailError}</span>
                  )}
                </div>
                <div className="subject form-inner">
                  <label htmlFor="subject">{lang.contact.formSubject}</label>
                  <input
                    onChange={SubjectInputHandler}
                    className={subjectError.length !== 0 ? "has-error" : ""}
                    value={subject}
                    type="text"
                    id="subject"
                  />
                  {subjectError.length !== 0 && (
                    <span className="error">{subjectError}</span>
                  )}
                </div>
                <div className="message form-inner">
                  <label htmlFor="message">{lang.contact.formMessage}</label>
                  <textarea
                    onChange={MessageInputHandler}
                    className={messageError.length !== 0 ? "has-error" : ""}
                    value={message}
                    placeholder={lang.contact.formMessagePlaceholder}
                    name="message"
                    id="message"
                    cols={30}
                    rows={10}
                  ></textarea>
                  {messageError.length !== 0 && (
                    <span className="error">{messageError}</span>
                  )}
                </div>
                <ConsentCheckboxes
                  consent={consent}
                  language={language}
                  setConsent={setConsent}
                />
                <div className="button-container">
                  <Button
                    disabled={Object.values(consent).some(
                      (value) => value === false,
                    )}
                    size="medium"
                    type="submit"
                    value={lang.contact.formButton}
                  />
                </div>
              </StyledForm>
            </div>
          </motion.div>

          <motion.div className="right" variants={FadeLeftAnimation}>
            <div className="info-container">
              <div className="info">
                <h4>AgwiPol Sp. z o.o.</h4>
                <p>NIP: 7010645357</p>
                <p>REGON: 366158708</p>
                <p>KRS: 0000655136</p>
                <a
                  href="https://wizytowka.rzetelnafirma.pl/6FPYR0E2"
                  target="blank"
                >
                  <img src={rzetelna} alt="Rzetelna Firma" loading="lazy" />
                </a>
              </div>
              <div className="email-info">
                <h4>{lang.contact.email}</h4>
                <a href="mailto:biuro@agwipol.pl">biuro@agwipol.pl</a>
              </div>
              <div className="phone-info">
                <h4>{lang.contact.phones}</h4>
                <a href="tel:+48574497249">+48 574 497 249</a>
                <a href="tel:+48578290011">+48 578 290 011</a>
              </div>
              <div className="address-info">
                <h4>{lang.contact.address}</h4>
                <div className="map">
                  <iframe
                    title="map"
                    loading="lazy"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d598.4509666089159!2d23.1379071!3d53.131666300000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471ffd9981a5a759%3A0xf581bdd20f3698dc!2sAgwiPol%20Sp.%20z%20o.o.!5e0!3m2!1sen!2spl!4v1784016981842!5m2!1sen!2spl"
                    frameBorder="0"
                  ></iframe>
                </div>
                <a href={mapsUrl} target="blank">
                  ul. Kard. St. Wyszyńskiego 2/1,
                  <br />
                  lok. 302-303,
                  <br />
                  15-888 Białystok
                </a>
              </div>
            </div>
          </motion.div>
        </StyledContainer>
      </StyledContact>

      <ScrollOnNavigation />
    </motion.div>
  );
};

export default Contact;
